<template>
  <div class="forgot-password-content">
    <div class="forgot-password-header">
      <h2>Recuperar contraseña</h2>
    </div>

    <p class="forgot-password-text">
      Ingresa tu RUT para enviarte las instrucciones de recuperación.
    </p>

    <form @submit.prevent="submit" class="forgot-password-form">
      <label for="reset-rut" class="email-label">RUT</label>
      <input
        id="reset-rut"
        v-model.trim="rut"
        type="text"
        class="email-input"
        placeholder="12.345.678-9"
        required
        autocomplete="off"
        @input="formatRut"
      />

      <p v-if="error" class="error-message">{{ error }}</p>
      <p v-if="success" class="success-message">{{ success }}</p>
      <div
        v-if="isTryCustomer"
        ref="recaptchaContainer"
        class="recaptcha-container"
      ></div>

      <button
        type="submit"
        class="submit-button"
        :disabled="loading || (isTryCustomer && !recaptchaToken)"
      >
        {{ loading ? "Enviando..." : "Enviar correo de recuperación" }}
      </button>

      <button type="button" class="back-button" @click="$emit('back')">
        Volver al inicio de sesión
      </button>
    </form>
  </div>
</template>

<script>
export default {
  name: "ForgotPasswordForm",
  props: {
    customer: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      rut: "",
      loading: false,
      error: "",
      success: "",
      recaptchaWidgetId: null,
      recaptchaToken: "",
    };
  },
  computed: {
    isTryCustomer() {
      const customerFromRoute = String(this.$route?.params?.customer || "");
      const customerFromProp = String(this.customer || "");
      const customerCode = (customerFromProp || customerFromRoute).toUpperCase();
      return customerCode === "TRY";
    },
  },
  mounted() {
    if (!this.isTryCustomer) return;
    this.$nextTick(() => {
      this.initRecaptcha();
    });
  },
  methods: {
    async initRecaptcha() {
      const siteKey = "6LeAw-ssAAAAAPWtYyUhqZui0VqTVSBYSqAgyZwY"
        .trim()
        .replace(/^"|"$/g, "");

      if (!siteKey) {
        this.error = "No se encontró la clave pública de reCAPTCHA.";
        return;
      }

      try {
        await this.loadRecaptchaScript();
      } catch (e) {
        this.error = "No se pudo cargar reCAPTCHA.";
        return;
      }

      if (!window.grecaptcha || !this.$refs.recaptchaContainer) {
        this.error = "No se pudo cargar reCAPTCHA.";
        return;
      }

      this.recaptchaWidgetId = window.grecaptcha.render(
        this.$refs.recaptchaContainer,
        {
          sitekey: siteKey,
          callback: (token) => {
            this.recaptchaToken = token;
            this.error = "";
          },
          "expired-callback": () => {
            this.recaptchaToken = "";
          },
          "error-callback": () => {
            this.recaptchaToken = "";
            this.error = "No se pudo validar el captcha.";
          },
        }
      );
    },
    loadRecaptchaScript() {
      if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const resolveWhenReady = () => {
          const maxAttempts = 100;
          let attempts = 0;
          const waitForGrecaptcha = () => {
            if (
              window.grecaptcha &&
              typeof window.grecaptcha.render === "function"
            ) {
              resolve();
              return;
            }

            attempts += 1;
            if (attempts >= maxAttempts) {
              reject(new Error("reCAPTCHA no estuvo listo a tiempo"));
              return;
            }
            setTimeout(waitForGrecaptcha, 100);
          };
          waitForGrecaptcha();
        };

        const existingScript = document.querySelector(
          'script[src*="www.google.com/recaptcha/api.js"]'
        );

        if (existingScript) {
          resolveWhenReady();
          return;
        }

        const callbackName = "__recaptchaOnLoadForgotPassword";
        window[callbackName] = () => {
          resolveWhenReady();
          try {
            delete window[callbackName];
          } catch (e) {
            window[callbackName] = undefined;
          }
        };

        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?onload=${callbackName}&render=explicit`;
        script.async = true;
        script.defer = true;
        script.onerror = () =>
          reject(new Error("No se pudo cargar el script de reCAPTCHA"));
        document.head.appendChild(script);
      });
    },
    formatRut() {
      const cleanRut = String(this.rut || "")
        .replace(/[^\dKk]/g, "")
        .slice(0, 9);
      if (!cleanRut) {
        this.rut = "";
        return;
      }

      if (cleanRut.length === 1) {
        this.rut = cleanRut;
        return;
      }

      const dv = cleanRut.slice(-1).toUpperCase();
      const body = cleanRut.slice(0, -1);
      const bodyWithDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      this.rut = `${bodyWithDots}-${dv}`;
    },
    async submit() {
      this.error = "";
      this.success = "";

      if (this.isTryCustomer && !this.recaptchaToken) {
        this.error = "Debes validar el captcha.";
        return;
      }

      this.loading = true;

      const cleanRut = String(this.rut || "").replace(/[^\dKk]/g, "");
      if (cleanRut.length < 8 || cleanRut.length > 9) {
        this.error = "El RUT debe tener 8 o 9 caracteres.";
        this.loading = false;
        return;
      }

      const customerPath = this.customer || this.$route?.params?.customer || "";
      const redirectUrl = `${window.location.origin}/app/${customerPath}/reset-password`;
      const customerId = this.$store.getters.getCustomerData?.customer_id;
      const rutForRequest = String(this.rut || "").replace(/\./g, "");
      const apiBaseUrl = String(process.env.VUE_APP_API_URL || "")
        .trim()
        .replace(/^"|"$/g, "")
        .replace(/\/+$/, "");

      try {
        const response = await this.axios.post(
          `${apiBaseUrl}/auth/password_recovery`,
          {
            rut: rutForRequest,
            customer_id: customerId,
            redirect_url: redirectUrl,
            ...(this.isTryCustomer
              ? { recaptcha_token: this.recaptchaToken }
              : {}),
          }
        );

        this.success =
          response?.data?.message ||
          "Te enviamos un correo con las instrucciones para restablecer tu contraseña.";

        if (
          this.isTryCustomer &&
          window.grecaptcha &&
          this.recaptchaWidgetId !== null &&
          this.recaptchaWidgetId !== undefined
        ) {
          window.grecaptcha.reset(this.recaptchaWidgetId);
        }
        this.recaptchaToken = "";
      } catch (e) {
        const errorData = e?.response?.data;
        const errorMessage =
          errorData?.errorMessage ||
          errorData?.error_message ||
          errorData?.message ||
          (Array.isArray(errorData?.errors) ? errorData.errors.join(", ") : "") ||
          "Error al recuperar contraseña";

        this.error = errorMessage;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.forgot-password-content {
  width: 100%;
}

.forgot-password-header {
  margin-bottom: 10px;
}

.forgot-password-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.3rem;
  font-weight: 700;
}

.forgot-password-text {
  margin: 0 0 16px;
  color: #6b7280;
  font-size: 0.95rem;
}

.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.email-label {
  color: #374151;
  font-size: 0.88rem;
  font-weight: 600;
}

.email-input {
  width: 100%;
  height: 46px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  outline: none;
}

.email-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.error-message {
  margin: 0;
  color: #dc2626;
  font-size: 0.87rem;
}

.success-message {
  margin: 0;
  color: #059669;
  font-size: 0.87rem;
}

.recaptcha-container {
  min-height: 78px;
}

.submit-button {
  height: 46px;
  margin-top: 4px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.back-button {
  height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #4b5563;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
}
</style>
